<template>
  <div class="library-index-view-component">
    <div class="library-content">
      <div class="library-navigation">
        <div class="navigation-items">
          <button
            v-for="parentTagging in parentTaggings"
            :key="parentTagging.id"
            class="parent-tagging"
            :class="{ active: parentTagging.id === activeTaggingId }"
            @click="setActiveTaggingId(parentTagging.id)"
          >
            {{ parentTagging.tag.name }}
          </button>
        </div>
      </div>
      <div class="library-ribbon-navigation">
        <button
          v-for="parentTagging in parentTaggings"
          :key="parentTagging.id"
          :class="{ active: parentTagging.id === activeTaggingId }"
          @click="setActiveTaggingId(parentTagging.id)"
        >
          {{ parentTagging.tag.name }}
        </button>
      </div>
      <div
        v-if="activeTagging"
        class="library-main"
      >
        <searchbar />
        <loading v-if="loading" />
        <div
          v-else
          class="library-tagging-content"
        >
          <template
            v-for="(childTagging, idx) in childTaggings"
          >
            <library-tag-carousel
              :key="`carousel-${childTagging.id}`"
              :assets="(taggingAssets[childTagging.id] || [])"
              :parent-tag="activeTagging.tag"
              :sort-order="sortOrder"
              :states="(assetStates[childTagging.id] || {})"
              :tag="childTagging.tag"
            />
            <divider
              v-if="idx < (childTaggings.length - 1)"
              :key="`divider-${childTagging.id}`"
              :spacing="'md'"
              :variant="'lightgrey'"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LibraryTagCarousel from '@/components/library/tag-carousel';
import Loading from '@/components/singletons/loading';
import Searchbar from '@/components/singletons/searchbar';

import GlobalMixin from '@/mixins/global';
import LibraryMixin from '@/mixins/library';
import Divider from '@/components/ui/divider.vue';


export default {
  name: 'LibraryIndex',
  components: { LibraryTagCarousel, Loading, Searchbar, Divider },
  mixins: [GlobalMixin, LibraryMixin],
  props: { },
  data() {
    return {
      activeTaggingId: null,
      loading: true,
      sortOrder: 'alphabetical',
      tagAssets: {},
    };
  },
  computed: {
    activeTagging() {
      return this.parentTaggings.find(lt => lt.id === this.activeTaggingId);
    },
    assetStates() {
      return Object.keys(this.tagAssets).reduce((acc, taggingId) => {
        const attachmentStates = (this.tagAssets[taggingId]?.attachments || [])
          .map(id => this.$store.getters['attachmentStates/get'][id])
          .filter(s => s);
        const topicStates = (this.tagAssets[taggingId]?.topics || [])
          .map(id => this.$store.getters['attachmentStates/get'][id])
          .filter(s => s);

        return {
          ...acc,
          [taggingId]: {
            attachments: attachmentStates,
            topics: topicStates,
          },
        };
      }, {});
    },
    childTaggings() {
      return this.libraryTaggings.filter(lt => lt.parentId === this.activeTaggingId);
    },
    libraryTaggings() {
      return this.$store.getters['tags/libraryTaggings'].map(t => {
        return {
          ...t,
          tag: this.$store.getters['tags/get'][t.tagId],
        };
      }).sort((a,b) => a.order - b.order);
    },
    parentTaggings() {
      return this.libraryTaggings.filter(lt => !lt.parentId);
    },
    sortOptions() {
      return [{ text: 'A - Z', value: 'alphabetical' },{ text: 'Z - A', value: 'reverse-alphabetical' }];
    },
    taggingAssets() {
      return Object.keys(this.tagAssets).reduce((acc, taggingId) => {
        const attachments = this.tagAssets[taggingId]?.attachments;
        const decks = this.tagAssets[taggingId]?.decks;
        const topics = this.tagAssets[taggingId]?.topics;

        const assets = [
          ...(attachments || []).map(id => this.$store.getters['attachments/get'][id]).filter(a => a),
          ...(decks || []).map(id => this.$store.getters['decks/get'][id]).filter(d => d),
          ...(topics|| []).map(id => this.$store.getters['topics/topics'][id]).filter(a => a),
        ];

        return {
          ...acc,
          [taggingId]: assets,
        };
      }, {});
    },
  },
  created() {
    let navItem = this.currentResource;
    if (!navItem.name) {
      const menuNavGroup = this.$store.getters['navigationGroups/menuNavigationGroup'];
      navItem = menuNavGroup?.navigationItems(this.$store).find(ni => ni.destination === 'library');
    }
    this.$analytics.events.viewedDashboard(navItem);
    this.$store.dispatch('flashcardStates/fetchStates', ['attachments', 'topics']);
  },
  mounted() {
    this.fetchAssets();
    if (this.$route.query?.tagging) {
      this.setActiveTaggingId(this.$route.query.tagging, false);
    } else if (this.parentTaggings?.[0]) {
      this.setActiveTaggingId(this.parentTaggings[0].id, false);
    }
  },
  methods: {
    fetchAssets() {
      this.loading = true;
      this.$store.getters['session/ajax'].get('/library').then(response => {
        const { attachments, decks, topics, records } = response.data;
        this.tagAssets = records || {};
        this.$store.commit('attachments/setAttachments', attachments);
        this.$store.commit('decks/set', decks);
        this.$store.commit('topics/setTopics', topics);
        this.loading = false;
      });
    },
    setActiveTaggingId(id, updateRoute = true) {
      if (updateRoute) {
        this.$router.replace({...this.$route, query: { ...this.$route.query, tagging: id }});
      }
      this.activeTaggingId = Number(id);
    },
    setSortOrder(sortOrder) {
      this.$analytics.events.sortedLibrary(sortOrder === 'alphabetical' ? 'Asc' : 'Desc');
      this.sortOrder = sortOrder;
    },
  },
};
</script>

<style lang="scss" scoped>
.library-index-view-component {
  padding: rem-calc(72) !important;

  .library-content {
    display: flex;
    margin: 0 auto;
    max-width: rem-calc(1200);

    .library-navigation {
      box-sizing: border-box;
      margin-top: 7rem; // should match height of library-tagging-content's margin top
      margin-right: rem-calc(128);
      width: rem-calc(160);

      .navigation-items {
        display: inline-block;

        .parent-tagging {
          color: #767676;
          background: transparent;
          border: 0;
          display: block;
          font-size: rem-calc(14);
          font-weight: $semi-bold;
          margin: rem-calc(16) 0;
          text-align: left;
          text-decoration: underline;

          &:hover {
            color: #000000;
          }

          &.active {
            color: #000000;
          }

          &:first-child {
            margin-top: rem-calc(8);
          }
        }
      }
    }
    .library-ribbon-navigation {
      display: none;
      padding: 0 1rem;
      border-bottom: 1px solid $elements-base;
      overflow-y: scroll;
      button {
        padding: 1rem 5px;
        margin-right: 1rem;
        background-color: transparent;
        font-size: 1rem;
        text-decoration: none;
        white-space: nowrap;
        color: #767676;

        &.active {
          font-weight: bold;
          color: #000000;
          border-bottom: 2px solid #000000;
        }
        &:last-child {
          margin: 0;
        }
      }
    }

    .library-main {
      max-width: rem-calc(768);
      position: relative;
      width: 100%;

      .library-tagging-content {
        margin-top: 7rem;
      }
    }
  }

  @include breakpoint(medium) {
    padding: rem-calc(0) !important;

    .library-content {
      flex-direction: column;

      .library-navigation {
        display: none;
      }
      .library-ribbon-navigation {
        display: flex;
        position: fixed;
        width: 100%;
        background-color: white;
        z-index: 100;
      }
      .library-main {
        width: initial;
        padding: 1rem;
        margin-top: rem-calc(53);
        /deep/ div.searchbar {
          margin: 0 2rem;
          width: initial;
        }
        .library-tagging-content {
          margin-top: 7rem;
        }
      }
    }
  }
}
</style>
